import React, {useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {filterMessages} from 'components/Filters/filterMessages'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {getFacetFilters} from 'modules/sapiSearch/selectors'
import {FilterUrlParams} from 'modules/search/types'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Pill} from '@vio/atlas/Pill'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  getReviewSentimentKey,
  reviewSentimentMessages
} from '@vio/core/src/hotel/reviewSentiments'

import {getIsFilterSelected} from '../utils'

interface Props
  extends Pick<
    ReturnType<typeof useFiltersForm>,
    'toggleMultiFilter' | 'toggleFilters' | 'getAppliedStarsLabel'
  > {
  filters: FilterUrlParams
  appliedFilters: FilterUrlParams
}

interface PopularFilterItem {
  id: string
  isSelected: boolean
  isDisabled?: boolean
  onClick: () => void
  label: string
  iconStart?: React.ReactElement
}

const POPULAR_FILTERS = {
  freeCancellation: '1',
  reviewScore: '8',
  facilities: [
    '1013', // Swimming Pool
    '6', // Parking
    '8', // Pet Friendly
    '299', // Free Wifi
    '5', // Restaurant
    '21', // Air conditioned
    '9' // Airport shuttle
  ],
  themes: [
    '1', // Beach / Coast
    '9' // Family
  ]
}

export const PopularFilters: React.FC<Props> = ({
  filters,
  appliedFilters,
  toggleMultiFilter,
  toggleFilters,
  getAppliedStarsLabel
}) => {
  const {formatMessage} = useIntl()
  const {
    search: {quickFilterPresets}
  } = useBrandCapabilities()

  const facetFilters = useSelector(getFacetFilters)

  const getFacilities = useCallback(
    (facilities: string[]) => {
      return facilities
        .filter(facetId => facetFilters?.facilities?.[facetId]?.label)
        .map(facetId => {
          const isDisabled =
            !facetFilters?.facilities?.[facetId]?.isAvailable &&
            !getIsFilterSelected(appliedFilters.facilities, facetId)

          return {
            id: `Facility:${facetId}`,
            isSelected: getIsFilterSelected(filters?.facilities || [], facetId),
            isDisabled,
            iconStart: undefined,
            onClick: () => toggleMultiFilter('facilities', facetId),
            label: facetFilters?.facilities?.[facetId]?.label ?? ''
          }
        })
    },
    [filters, toggleMultiFilter, facetFilters, appliedFilters]
  )

  const getThemes = useCallback(
    (themes: string[]) => {
      return themes.map(themeId => {
        const isDisabled =
          !facetFilters?.themes?.[themeId]?.isAvailable &&
          !getIsFilterSelected(appliedFilters.themes, themeId)

        return {
          id: `Theme:${themeId}`,
          isSelected: getIsFilterSelected(filters?.themes, themeId),
          isDisabled,
          iconStart: undefined,
          onClick: () => toggleMultiFilter('themes', themeId),
          label: facetFilters?.themes?.[themeId]?.label ?? ''
        }
      })
    },
    [filters, toggleMultiFilter, facetFilters, appliedFilters]
  )

  const getStarRatings = useCallback(
    (starRatings: string[]) => {
      if (!starRatings.length) return []

      const isSelected = starRatings.every(starRating =>
        getIsFilterSelected(filters?.starRatings, starRating)
      )

      const isApplied = starRatings.every(starRating =>
        getIsFilterSelected(appliedFilters.starRatings, starRating)
      )

      const isAvailable = starRatings.some(
        starRating => facetFilters?.starRatings?.[starRating]?.isAvailable
      )

      return [
        {
          id: `StarRatings:${starRatings.join('-')}`,
          isSelected,
          onClick: () =>
            toggleFilters({starRatings: isSelected ? undefined : starRatings}),
          iconStart: <Icon name={'Star'} />,
          isDisabled: !isAvailable && !isApplied,
          label: getAppliedStarsLabel(starRatings)
        }
      ]
    },
    [filters, toggleFilters, getAppliedStarsLabel, facetFilters, appliedFilters]
  )

  const getFreeCancellation = useCallback(() => {
    const isSelected = filters?.freeCancellation === '1'

    return [
      {
        id: 'FreeCancellation',
        isSelected,
        iconStart: undefined,
        onClick: () =>
          toggleFilters({freeCancellation: isSelected ? undefined : '1'}),
        label: formatMessage(filterMessages.freeCancellationTitle),
        isDisabled: false
      }
    ]
  }, [filters, toggleFilters, formatMessage])

  const getReviewScore = useCallback(
    (reviewScore: string) => {
      const isSelected = filters?.reviewScore === reviewScore

      const sentimentMessageKey = getReviewSentimentKey(Number(reviewScore))
      const translatedSentiment = sentimentMessageKey
        ? reviewSentimentMessages[sentimentMessageKey]
        : null

      return [
        {
          id: 'ReviewScore',
          isSelected,
          isDisabled: isSelected && filters?.reviewScore !== reviewScore,
          iconStart: undefined,
          onClick: () =>
            toggleFilters({
              reviewScore: isSelected ? undefined : reviewScore
            }),
          label: `${formatMessage(translatedSentiment)} ${filters?.reviewScore ?? reviewScore}+`
        }
      ]
    },
    [filters, toggleFilters, formatMessage]
  )

  const popularFiltersList: PopularFilterItem[] = useMemo(() => {
    return [
      ...getFreeCancellation(),
      ...getStarRatings(quickFilterPresets.starRatings),
      ...getReviewScore(POPULAR_FILTERS.reviewScore),
      ...getFacilities(POPULAR_FILTERS.facilities.slice(0, 2)),
      ...getThemes(POPULAR_FILTERS.themes),
      ...getFacilities(POPULAR_FILTERS.facilities.slice(3))
    ]
  }, [
    getFacilities,
    getThemes,
    getStarRatings,
    getFreeCancellation,
    getReviewScore,
    quickFilterPresets.starRatings
  ])

  return (
    <Box display="flex" columnGap="s300" rowGap="s300" flexWrap="wrap">
      {popularFiltersList
        .sort((a, b) => Number(!!a.isDisabled) - Number(!!b.isDisabled))
        .filter(filter => Boolean(filter.label))
        .map(filter => {
          return (
            <Pill
              size="lg"
              key={filter.id}
              isSelected={filter.isSelected}
              isDisabled={filter.isDisabled}
              iconStart={filter.iconStart}
              onClick={filter.onClick}
            >
              {filter.label}
            </Pill>
          )
        })}
    </Box>
  )
}
